import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HbrCard, HbrIcon, HbrSpinner, HbrTag } from '@harbor/elements-react-standalone';
import {
  arrowSquareOut,
  checkCircle,
  clock,
  downloadSimple,
  info,
  paperclip,
} from '@harbor/elements/icons';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';
import { fetchItems } from '../../../support/queries';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import JourneyMapTaskSubmissionForm from './JourneyMapTaskSubmissionForm';
import {
  formatFileSize,
  listJourneyMapFiles,
  openJourneyMapFile,
  JourneyMapFileRecord,
} from '../../../support/journeyMap/files';
import { shouldTrackExternalLaunch } from '../../../support/journeyMap/externalLaunch';
import {
  hasToolboxDbRecord,
  isResourceFileContentRefType,
  isVideoContentRefType,
} from '../../../support/journeyMap/contentSources';
import ResourceFileViewer from './ResourceFileViewer';
import JourneyMapVideoPlayer from './JourneyMapVideoPlayer';

type Props = {
  open: boolean;
  item: any | null;
  items?: any[];
  assignmentId?: string | number | null;
  currentUserId?: string | number | null;
  completionByItemId?: Record<string | number, boolean>;
  completionReasonByItemId?: Record<string | number, string>;
  pendingValidationByItemId?: Record<string | number, boolean>;
  syncStatusByItemId?: Record<string | number, string | null>;
  statusReasonByItemId?: Record<string | number, string>;
  onClose: () => void;
  onCompleteItem: (item: any) => Promise<void> | void;
  onNextItem: (item: any) => void;
  onPreviousItem: (item: any) => void;
  /**
   * Opens an external item and, for externally synced providers, records the launch so the
   * step can show as awaiting confirmation until the nightly sync reports the completion.
   */
  onLaunchItem?: (item: any) => Promise<void> | void;
  /**
   * Fired when a self-hosted video crosses its watch-coverage threshold. The video-progress
   * endpoint has already credited the completion server-side, so this only needs to refresh the
   * page's progress view — it must NOT re-run the generic completion write (which records an
   * `external_sync` source and would double-write).
   */
  onVideoCompleted?: (item: any) => void;
  onLaunchCourseItem?: (item: any) => void;
  onTaskSubmissionSaved?: (item: any, submission: any) => Promise<void> | void;
  renderCourseItem?: (
    item: any,
    opts: {
      alreadyCompleted: boolean;
      completionReason?: string;
      onComplete: () => Promise<void> | void;
      onLaunchLearning?: () => void;
    }
  ) => React.ReactNode;
};

const getTypeLabel = (itemType?: string) => {
  switch (itemType) {
    case 'section':
      return 'Section';
    case 'assessment':
      return 'Assessment';
    case 'task':
      return 'Task';
    default:
      return 'Content';
  }
};

const isCourseProvider = (item: any) => {
  const provider = String(item?.provider || '').toLowerCase();
  return provider.includes('moodle') || provider.includes('cyber');
};

const isDcloudProvider = (item: any) => {
  const provider = String(item?.provider || '').toLowerCase();
  const contentRefType = String(item?.content_ref_type || '').toLowerCase();
  return provider.includes('dcloud') || contentRefType === 'dcloud_catalog';
};

const isJourneyMapReferenceItem = (item: any) => {
  return String(item?.content_ref_type || '').toLowerCase() === 'journey_map';
};

const isExternalLinkItem = (item: any) => !!item?.launch_url;

/**
 * A `resource_file` step points at a file-backed `resource` row: uploaded once, referenced from
 * any number of steps, and rendered in place by `ResourceFileViewer`.
 */
const isResourceFileItem = (item: any) =>
  isResourceFileContentRefType(item?.content_ref_type);

/**
 * A `video` step is a file-backed `resource` (stored like `resource_file`) that renders in the
 * tracking `<video>` player rather than the generic file viewer. Recognised by either the content
 * ref type or the provider, so a step keyed only on the provider still resolves as a video.
 */
const isVideoItem = (item: any) =>
  isVideoContentRefType(item?.content_ref_type) ||
  String(item?.provider || '').trim().toLowerCase() === 'video';

/**
 * A legacy `file` step points at a `JourneyMapFile` row rather than a toolbox_db content
 * record, so it is resolved through the attachment endpoints instead of the generic content
 * loader.
 *
 * The `provider === 'file'` fallback must not swallow `resource_file`. Both types sit under the
 * File provider but their ids come from different tables, so treating a `resource.id` as a
 * `journey_map_file.id` would not error — it would hand back an unrelated file that happens to
 * share the number.
 */
const isFileItem = (item: any) => {
  if (isResourceFileItem(item)) return false;
  if (String(item?.content_ref_type || '').toLowerCase() === 'file') return true;
  return String(item?.provider || '').trim().toLowerCase() === 'file';
};

const isTaskItem = (item: any) => String(item?.item_type || '').toLowerCase() === 'task';

const isShadowingTaskItem = (item: any) => {
  const metadata =
    typeof item?.metadata === 'string'
      ? (() => {
        try {
          return JSON.parse(item.metadata);
        } catch {
          return {};
        }
      })()
      : item?.metadata || {};

  return (
    isTaskItem(item) &&
    String(metadata?.task_type || '').toLowerCase() === 'shadowing'
  );
};

const isContentItem = (item: any) => {
  if (isTaskItem(item)) return false;
  if (isCourseProvider(item)) return false;
  if (isExternalLinkItem(item)) return false;
  if (isJourneyMapReferenceItem(item)) return false;
  if (isFileItem(item)) return false;
  // `ResourceFileViewer` fetches what it needs (and a signed URL besides), so the generic
  // content loader would only duplicate the request.
  if (isResourceFileItem(item)) return false;
  // The video player fetches its own signed URL, so the generic loader would only duplicate it.
  if (isVideoItem(item)) return false;
  return true;
};

const JourneyMapItemModal: React.FC<Props> = ({
                                                open,
                                                item,
                                                items = [],
                                                assignmentId = null,
                                                currentUserId = null,
                                                completionByItemId = {},
                                                completionReasonByItemId = {},
                                                pendingValidationByItemId = {},
                                                syncStatusByItemId = {},
                                                statusReasonByItemId = {},
                                                onClose,
                                                onCompleteItem,
                                                onNextItem,
                                                onPreviousItem,
                                                onLaunchItem,
                                                onVideoCompleted,
                                                onLaunchCourseItem,
                                                onTaskSubmissionSaved,
                                                renderCourseItem,
                                              }) => {
  const navigate = useNavigate();

  const [contentRecord, setContentRecord] = useState<any | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [externalOpened, setExternalOpened] = useState(false);

  const [fileRecord, setFileRecord] = useState<JourneyMapFileRecord | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [referencedJourneyProgressLoading, setReferencedJourneyProgressLoading] = useState(false);
  const [referencedJourneyProgress, setReferencedJourneyProgress] = useState<{
    assigned: boolean;
    completed: boolean;
    progressPercent: number;
    assignmentStatusLabel: string;
  } | null>(null);

  const itemId = item?.id != null ? String(item.id) : null;
  const isCompleted = itemId ? !!completionByItemId[itemId] : false;
  const isPendingValidation = itemId ? !!pendingValidationByItemId[itemId] : false;
  const completionReason = itemId ? completionReasonByItemId[itemId] : undefined;
  const statusReason = itemId ? statusReasonByItemId[itemId] : undefined;
  const syncStatus = itemId
    ? String(syncStatusByItemId[itemId] || '').trim().toLowerCase()
    : '';

  // The launch was recorded but no confirmation ever arrived within the grace period.
  const isStalePending = syncStatus === 'stale';

  // Mindtickle (and any other provider opted into pending validation): the completion is
  // reported by a nightly sync, so this modal must never claim the step is finished on launch.
  const isLaunchTracked = useMemo(() => shouldTrackExternalLaunch(item), [item]);

  const orderedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [items]);

  const currentIndex = useMemo(() => {
    if (!item) return -1;
    return orderedItems.findIndex((i) => String(i.id) === String(item.id));
  }, [orderedItems, item]);

  const previousItem = currentIndex > 0 ? orderedItems[currentIndex - 1] : null;
  const nextItem =
    currentIndex > -1 && currentIndex < orderedItems.length - 1
      ? orderedItems[currentIndex + 1]
      : null;

  const referencedJourneyMapId = useMemo(() => {
    if (!isJourneyMapReferenceItem(item)) return null;
    if (item?.content_ref_id == null) return null;
    return String(item.content_ref_id);
  }, [item]);

  const taskSubmission = useMemo(() => {
    return item?.task_submission || item?.progress?.task_submission || null;
  }, [item]);

  /**
   * The builder caches the attachment's metadata on the item, so the common case needs no
   * lookup at all: the id in `content_ref_id` is enough to ask for a signed URL on click.
   */
  /**
   * The resource id and cached display fields for a `resource_file` step.
   *
   * The cache is a convenience for labelling the card before any request goes out; the viewer
   * re-reads the authoritative values from the download endpoint either way, so a step authored
   * without it (or whose file was replaced) still renders correctly.
   */
  const resourceFileRef = useMemo(() => {
    if (!isResourceFileItem(item)) return null;

    const cached = item?.provider_config?.file || null;
    const resourceId = Number(cached?.resource_id ?? item?.content_ref_id);

    if (!Number.isFinite(resourceId) || resourceId <= 0) return null;

    return {
      id: resourceId,
      file_name: String(cached?.file_name || ''),
      content_type: String(cached?.content_type || ''),
      size_bytes: Number(cached?.size_bytes || 0),
      render_kind: String(cached?.render_kind || '') as any,
      version: Number(cached?.version || 0) || null,
    };
  }, [item]);

  /**
   * The resource id, watch threshold and cache version for a `video` step. Stored exactly like
   * `resource_file` (bytes in `provider_config.file`), plus a `completion_threshold_pct`.
   */
  const videoRef = useMemo(() => {
    if (!isVideoItem(item)) return null;

    const cached = item?.provider_config?.file || null;
    const resourceId = Number(cached?.resource_id ?? item?.content_ref_id);

    if (!Number.isFinite(resourceId) || resourceId <= 0) return null;

    const threshold = Number(item?.provider_config?.completion_threshold_pct);

    // The server stores the last playhead in the progress row's evidence; reopening the step
    // resumes from there (see JourneyMapVideoPlayer's resumePositionSec).
    const resumeRaw = Number(item?.progress?.evidence?.video?.last_position_sec);

    return {
      id: resourceId,
      thresholdPct: Number.isFinite(threshold) && threshold > 0 ? threshold : undefined,
      version: Number(cached?.version || 0) || null,
      resumePositionSec: Number.isFinite(resumeRaw) && resumeRaw > 0 ? resumeRaw : null,
    };
  }, [item]);

  /**
   * A plain `resource` step whose resource turns out to be a file.
   *
   * `content_ref_type='resource'` says nothing about whether the row is a link, a document or an
   * uploaded file, so this is decided from the fetched record's own `is_file` rather than from
   * the step. Without it, pointing a Toolbox Resource step at a file resource would render the
   * empty markdown body of a row whose content lives in S3.
   */
  const contentRecordFile = useMemo(() => {
    if (!contentRecord?.is_file) return null;

    const resourceId = Number(contentRecord.id);
    if (!Number.isFinite(resourceId) || resourceId <= 0) return null;

    return {
      id: resourceId,
      file_name: String(contentRecord.file_name || contentRecord.name || ''),
      content_type: String(contentRecord.content_type || ''),
      size_bytes: Number(contentRecord.size_bytes || 0),
      render_kind: String(contentRecord.render_kind || '') as any,
      version: Number(contentRecord.version || 0) || null,
    };
  }, [contentRecord]);

  const cachedFile = useMemo(() => {
    if (!isFileItem(item)) return null;

    const cached = item?.provider_config?.file || null;
    const refId = item?.content_ref_id;
    const fileId = Number(cached?.file_id ?? refId);

    if (!Number.isFinite(fileId) || fileId <= 0) return null;

    return {
      id: fileId,
      file_name: String(cached?.file_name || ''),
      content_type: String(cached?.content_type || ''),
      size_bytes: Number(cached?.size_bytes || 0),
      is_previewable: cached?.is_previewable === true,
    };
  }, [item]);

  // Only reached when the cache is missing (an item authored before the dropper, or one whose
  // file was replaced by another author) and the map id is available to scope the lookup.
  useEffect(() => {
    let cancelled = false;

    const loadFile = async () => {
      setFileRecord(null);
      setFileError(null);

      if (!open || !cachedFile?.id) return;
      if (cachedFile.file_name) return;

      const journeyMapId = item?.journey_map ?? item?.journey_map_id ?? null;
      if (!journeyMapId) return;

      setFileLoading(true);
      try {
        const rows = await listJourneyMapFiles(journeyMapId, { includeInactive: true });
        if (cancelled) return;

        setFileRecord(rows.find((row) => Number(row.id) === cachedFile.id) || null);
      } catch {
        // Falling back to the id-only label is better than blocking the download button.
      } finally {
        if (!cancelled) setFileLoading(false);
      }
    };

    void loadFile();
    return () => {
      cancelled = true;
    };
  }, [open, item, cachedFile]);

  const resolvedFile = useMemo(() => {
    if (!cachedFile) return null;

    return {
      id: cachedFile.id,
      file_name: fileRecord?.file_name || cachedFile.file_name || `File ${cachedFile.id}`,
      content_type: fileRecord?.content_type || cachedFile.content_type,
      size_bytes: fileRecord?.size_bytes ?? cachedFile.size_bytes,
      is_previewable: fileRecord?.is_previewable ?? cachedFile.is_previewable,
      is_active: fileRecord ? fileRecord.is_active !== false : true,
    };
  }, [cachedFile, fileRecord]);

  const fileDetailLine = useMemo(() => {
    if (!resolvedFile) return '';

    return [formatFileSize(resolvedFile.size_bytes), resolvedFile.content_type]
      .filter(Boolean)
      .join(' · ');
  }, [resolvedFile]);

  useEffect(() => {
    let cancelled = false;

    const loadReferencedJourneyProgress = async () => {
      setReferencedJourneyProgress(null);

      if (!open || !referencedJourneyMapId) return;

      setReferencedJourneyProgressLoading(true);
      try {
        const assignments = await fetchItems(
          'db/journey-map-assignment/',
          { journey_map: referencedJourneyMapId },
          { includePageSize: false }
        ).catch(() => []);

        const rows = Array.isArray(assignments) ? assignments : [];
        const activeAssignment =
          rows.find((r: any) => String(r?.assignment_bucket || '').toLowerCase() === 'assigned') ||
          rows[0] ||
          null;

        if (!activeAssignment?.id) {
          if (!cancelled) {
            setReferencedJourneyProgress({
              assigned: false,
              completed: false,
              progressPercent: 0,
              assignmentStatusLabel: 'Not Assigned',
            });
          }
          return;
        }

        const progressPercent = Number(activeAssignment?.progress_percent || 0);
        const bucket = String(activeAssignment?.assignment_bucket || '').toLowerCase();
        const statusName = String(activeAssignment?.status_name || '').trim();

        const completed =
          bucket === 'completed' ||
          statusName.toLowerCase() === 'completed' ||
          progressPercent >= 100;

        const statusLabel =
          completed
            ? 'Completed'
            : bucket === 'assigned'
              ? 'In Progress'
              : statusName || 'Assigned';

        if (!cancelled) {
          setReferencedJourneyProgress({
            assigned: true,
            completed,
            progressPercent: Math.max(0, Math.min(100, progressPercent)),
            assignmentStatusLabel: statusLabel,
          });
        }
      } finally {
        if (!cancelled) setReferencedJourneyProgressLoading(false);
      }
    };

    void loadReferencedJourneyProgress();
    return () => {
      cancelled = true;
    };
  }, [open, referencedJourneyMapId]);

  useEffect(() => {
    let cancelled = false;

    const loadContent = async () => {
      setHasReachedBottom(false);
      setExternalOpened(false);
      setContentRecord(null);

      if (!open || !item || !isContentItem(item)) return;
      if (!item?.content_ref_type || !item?.content_ref_id) return;
      // No toolbox_db model behind the type means `db/{type}/{id}/` is not a real endpoint.
      if (!hasToolboxDbRecord(item.content_ref_type)) return;

      setContentLoading(true);
      try {
        const resourceName = String(item.content_ref_type).replace(/_/g, '-');
        const data = await fetchItems(
          `db/${resourceName}/${item.content_ref_id}/`,
          {},
          { includePageSize: false }
        );
        if (!cancelled) {
          setContentRecord(data || null);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    };

    void loadContent();
    return () => {
      cancelled = true;
    };
  }, [open, item]);

  const getBestReadableContent = (record: any, item: any): string => {
    const candidates = [
      record?.markdown,
      record?.content_markdown,
      record?.content,
      record?.body,
      record?.description,
      record?.instructions,
      item?.markdown,
      item?.content,
      item?.body,
      item?.description,
    ];

    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  };

  const contentBodyText = useMemo(() => {
    return getBestReadableContent(contentRecord, item);
  }, [contentRecord, item]);

  const hasReadableContentBody = useMemo(() => {
    return contentBodyText.trim().length > 0;
  }, [contentBodyText]);

  const canGoNext = useMemo(() => {
    if (!item) return false;
    return isCompleted;
  }, [item, isCompleted]);

  const canMarkContentComplete = useMemo(() => {
    if (!item || !isContentItem(item)) return false;
    if (isCompleted) return false;
    // A file resource shows the viewer instead of the scrollable body, so there is nothing to
    // scroll to the bottom of. Gating on `hasReachedBottom` here would dead-end any file whose
    // row also carries a description — the scroll container that sets that flag is not rendered.
    if (contentRecordFile) return true;
    if (!hasReadableContentBody) return true;
    return hasReachedBottom;
  }, [item, isCompleted, contentRecordFile, hasReadableContentBody, hasReachedBottom]);

  const canAlwaysLaunchExternal = useMemo(() => {
    return isDcloudProvider(item);
  }, [item]);

  const handleOpenJourneyMap = async () => {
    if (!referencedJourneyMapId) return;
    navigate(`/enablement/journey-map/${referencedJourneyMapId}`);
    onClose();
  };

  const handleOpenExternal = async () => {
    if (!item?.launch_url) return;

    /**
     * Externally synced items must not be completed here. Their completion is owned by the
     * nightly sync, so the launch is recorded instead and the step reads as awaiting
     * confirmation. Relaunching an unconfirmed item re-records it, which is the only lever
     * available for keeping the displayed state close to reality between sync runs.
     */
    if (isLaunchTracked) {
      setExternalOpened(true);

      if (!onLaunchItem) {
        // No tracking handler wired up: still open the course rather than dead-end the learner.
        window.open(item.launch_url, '_blank', 'noopener,noreferrer');
        return;
      }

      await onLaunchItem(item);
      return;
    }

    window.open(item.launch_url, '_blank', 'noopener,noreferrer');
    setExternalOpened(true);

    if (!isCompleted) {
      await onCompleteItem(item);
    }
  };

  const handleOpenFile = async (inline: boolean) => {
    if (!resolvedFile?.id) return;

    setFileError(null);
    setFileBusy(true);
    try {
      await openJourneyMapFile(
        {
          id: resolvedFile.id,
          file_name: resolvedFile.file_name,
          is_previewable: resolvedFile.is_previewable,
        },
        { inline }
      );
    } catch (err: any) {
      setFileError(String(err?.message || 'Unable to open this file.'));
    } finally {
      setFileBusy(false);
    }
  };

  const handleMarkContentComplete = async () => {
    await onCompleteItem(item);
  };

  const title = item?.name || 'Journey Item';

  return (
    <StbModal
      open={open}
      title={title}
      size="x-large"
      hideCancel
      submitLabel="Close"
      onClose={async () => onClose()}
      onSubmit={async () => onClose()}
      suppressCloseOnSubmit={false}
      scrollable
    >
      {!item ? null : (
        <div
          className="hbr-css__layout-col-md"
          style={{
            minHeight: 0,
            maxHeight: '78vh',
          }}
        >
          <div className="hbr-css__layout-row-sm hbr-css__layout-align-center hbr-css__layout-wrap">
            <HbrTag size="small">{getTypeLabel(item?.item_type)}</HbrTag>
            {item?.provider ? <HbrTag size="small">{item.provider}</HbrTag> : null}
            {isCompleted ? <HbrTag size="small">Completed</HbrTag> : null}
            {isPendingValidation ? (
              <HbrTag size="small">
                {isStalePending
                  ? 'Not Confirmed'
                  : isLaunchTracked
                    ? 'Awaiting Confirmation'
                    : 'Pending Validation'}
              </HbrTag>
            ) : null}
            {item?.estimated_duration_minutes ? (
              <HbrTag size="small">
                <span className="hbr-css__layout-row-3xs hbr-css__layout-align-center">
                  <HbrIcon asset={clock} />
                  {item.estimated_duration_minutes} min
                </span>
              </HbrTag>
            ) : null}
          </div>

          {item?.description ? (
            <div className="hbr-css__text-secondary-sm">{item.description}</div>
          ) : null}

          {statusReason && !isCompleted ? (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                <HbrIcon asset={info} />
                {statusReason}
              </div>
            </HbrCard>
          ) : null}

          <div
            style={{
              minHeight: 0,
              overflowY: 'auto',
              maxHeight: '58vh',
              paddingRight: 4,
            }}
          >
            {isTaskItem(item) && isShadowingTaskItem(item) ? (
              <JourneyMapTaskSubmissionForm
                item={item}
                assignmentId={assignmentId}
                currentUserId={currentUserId}
                existingSubmission={taskSubmission}
                onSaved={async (submission) => {
                  await onTaskSubmissionSaved?.(item, submission);
                }}
              />
            ) : isTaskItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">Task Item</div>
                  <div className="hbr-css__text-secondary-sm">
                    This task type does not yet have a custom renderer.
                  </div>
                </div>
              </HbrCard>
            ) : isCourseProvider(item) ? (
              renderCourseItem ? (
                renderCourseItem(item, {
                  alreadyCompleted: isCompleted,
                  completionReason,
                  onComplete: async () => {
                    await onCompleteItem(item);
                  },
                  onLaunchLearning: () => {
                    onLaunchCourseItem?.(item);
                    onClose();
                  },
                })
              ) : (
                <HbrCard
                  style={
                    {
                      '--box-shadow': 'none',
                      '--border-width': '2px',
                    } as React.CSSProperties
                  }
                >
                  <div className="hbr-css__layout-col-sm">
                    <div className="hbr-css__text-primary-sm-semibold">
                      Course content renderer not configured.
                    </div>
                    <div className="hbr-css__text-secondary-sm">
                      This item is backed by Moodle/Cyber Academy content.
                    </div>
                  </div>
                </HbrCard>
              )
            ) : isJourneyMapReferenceItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">Referenced Journey Map</div>
                  <div className="hbr-css__text-secondary-sm">
                    Completion for this step is based on the referenced journey map status.
                  </div>

                  {referencedJourneyProgressLoading ? (
                    <HbrSpinner size="small">
                      <div slot="label">Loading referenced progress...</div>
                    </HbrSpinner>
                  ) : referencedJourneyProgress ? (
                    <div className="hbr-css__layout-col-3xs">
                      <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
                        <HbrTag size="small">
                          {referencedJourneyProgress.assignmentStatusLabel}
                        </HbrTag>
                        <HbrTag size="small">
                          {Math.round(referencedJourneyProgress.progressPercent)}% complete
                        </HbrTag>
                      </div>

                      <div
                        style={{
                          width: '100%',
                          height: '10px',
                          borderRadius: '999px',
                          backgroundColor: 'var(--hbr-color-neutral-bg-weak)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100, referencedJourneyProgress.progressPercent)
                            )}%`,
                            height: '100%',
                            backgroundColor: referencedJourneyProgress.completed
                              ? 'var(--hbr-color-success)'
                              : 'var(--hbr-color-info)',
                            borderRadius: '999px',
                            transition: 'width 250ms ease',
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="hbr-css__text-secondary-sm">
                      Unable to load referenced journey map progress.
                    </div>
                  )}

                  <div>
                    <StbButton
                      role="default"
                      onClick={handleOpenJourneyMap}
                      icon={arrowSquareOut}
                      disabled={!referencedJourneyMapId}
                    >
                      Launch Journey Map
                    </StbButton>
                  </div>
                </div>
              </HbrCard>
            ) : isExternalLinkItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">
                    {isLaunchTracked ? 'External Course' : 'External Content'}
                  </div>
                  <div className="hbr-css__text-secondary-sm">
                    Open this content in a new tab.
                    {isLaunchTracked
                      ? ' Completion for this step is reported by a nightly sync from the' +
                        ' external system, so it stays in an awaiting-confirmation state until' +
                        ' that data lands. You can relaunch it at any time.'
                      : !canAlwaysLaunchExternal
                        ? ' After launch, this step will be marked complete.'
                        : ' This item can be relaunched even after completion.'}
                  </div>
                  <div>
                    <StbButton
                      role="default"
                      onClick={handleOpenExternal}
                      icon={arrowSquareOut}
                      disabled={!canAlwaysLaunchExternal && !isLaunchTracked && isCompleted}
                    >
                      {isLaunchTracked
                        ? isCompleted || externalOpened || isPendingValidation
                          ? 'Relaunch Course'
                          : 'Launch Course'
                        : isCompleted
                          ? canAlwaysLaunchExternal
                            ? 'Open External Link'
                            : 'Completed'
                          : 'Open External Link'}
                    </StbButton>
                  </div>
                  {isLaunchTracked ? (
                    isCompleted ? (
                      <div className="hbr-css__layout-row-3xs hbr-css__text-secondary-sm">
                        <HbrIcon asset={checkCircle} />
                        Completion confirmed by the external sync.
                      </div>
                    ) : externalOpened || isPendingValidation ? (
                      <div className="hbr-css__layout-row-3xs hbr-css__text-secondary-sm">
                        <HbrIcon asset={clock} />
                        {isStalePending
                          ? 'No completion has come back yet. Relaunch to finish it, or ask for a manual review.'
                          : 'Launch recorded. Completion will be confirmed by the next nightly sync.'}
                      </div>
                    ) : null
                  ) : externalOpened || isCompleted ? (
                    <div className="hbr-css__layout-row-3xs hbr-css__text-secondary-sm">
                      <HbrIcon asset={checkCircle} />
                      Item marked complete.
                    </div>
                  ) : null}
                </div>
              </HbrCard>
            ) : isVideoItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">Video</div>

                  {videoRef ? (
                    <JourneyMapVideoPlayer
                      resourceId={videoRef.id}
                      item={item}
                      thresholdPct={videoRef.thresholdPct}
                      assignmentId={assignmentId}
                      version={videoRef.version}
                      resumePositionSec={videoRef.resumePositionSec}
                      onVideoCompleted={onVideoCompleted}
                    />
                  ) : (
                    <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                      <HbrIcon asset={info} />
                      No video has been attached to this step yet.
                    </div>
                  )}
                </div>
              </HbrCard>
            ) : isResourceFileItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">File</div>

                  {resourceFileRef ? (
                    <ResourceFileViewer
                      resourceId={resourceFileRef.id}
                      fileName={resourceFileRef.file_name}
                      contentType={resourceFileRef.content_type}
                      sizeBytes={resourceFileRef.size_bytes}
                      renderKind={resourceFileRef.render_kind}
                      version={resourceFileRef.version}
                    />
                  ) : (
                    <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                      <HbrIcon asset={info} />
                      No file has been attached to this step yet.
                    </div>
                  )}
                </div>
              </HbrCard>
            ) : isFileItem(item) ? (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__text-primary-sm-semibold">Attached File</div>

                  {fileLoading ? (
                    <HbrSpinner size="small">
                      <div slot="label">Loading file details...</div>
                    </HbrSpinner>
                  ) : !resolvedFile ? (
                    <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                      <HbrIcon asset={info} />
                      No file has been attached to this step yet.
                    </div>
                  ) : (
                    <>
                      <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                        <HbrIcon asset={paperclip} />
                        <span style={{ wordBreak: 'break-all' }}>{resolvedFile.file_name}</span>
                        {fileDetailLine ? <span>· {fileDetailLine}</span> : null}
                      </div>

                      {!resolvedFile.is_active ? (
                        <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                          <HbrIcon asset={info} />
                          This file is no longer available. Ask the journey owner to re-attach it.
                        </div>
                      ) : null}

                      <div className="hbr-css__layout-row-sm hbr-css__layout-wrap">
                        <StbButton
                          role="default"
                          icon={downloadSimple}
                          onClick={async () => handleOpenFile(false)}
                          disabled={fileBusy || !resolvedFile.is_active}
                        >
                          {fileBusy ? 'Preparing...' : 'Download File'}
                        </StbButton>

                        {resolvedFile.is_previewable ? (
                          <StbButton
                            role="default"
                            icon={arrowSquareOut}
                            onClick={async () => handleOpenFile(true)}
                            disabled={fileBusy || !resolvedFile.is_active}
                          >
                            Preview
                          </StbButton>
                        ) : null}
                      </div>

                      {fileError ? (
                        <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                          <HbrIcon asset={info} />
                          {fileError}
                        </div>
                      ) : null}
                    </>
                  )}

                  <div>
                    <StbButton
                      role="default"
                      disabled={isCompleted}
                      onClick={handleMarkContentComplete}
                    >
                      {isCompleted ? 'Completed' : 'Mark Complete'}
                    </StbButton>
                  </div>
                </div>
              </HbrCard>
            ) : (
              <HbrCard
                style={
                  {
                    '--box-shadow': 'none',
                    '--border-width': '2px',
                  } as React.CSSProperties
                }
              >
                <div className="hbr-css__layout-col-sm">
                  <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                    <HbrIcon asset={info} />
                    {contentRecordFile
                      ? 'Preview or download the file, then mark this item complete.'
                      : hasReadableContentBody
                        ? 'Read the full content to enable completion.'
                        : 'No readable content body is available, so you can mark this item complete manually.'}
                  </div>

                  {contentLoading ? (
                    <HbrSpinner size="small">
                      <div slot="label">Loading content...</div>
                    </HbrSpinner>
                  ) : contentRecordFile ? (
                    <ResourceFileViewer
                      resourceId={contentRecordFile.id}
                      fileName={contentRecordFile.file_name}
                      contentType={contentRecordFile.content_type}
                      sizeBytes={contentRecordFile.size_bytes}
                      renderKind={contentRecordFile.render_kind}
                      version={contentRecordFile.version}
                    />
                  ) : (
                    <div
                      style={{
                        maxHeight: '42vh',
                        overflowY: 'auto',
                        padding: '12px',
                        border: '1px solid var(--hbr-color-neutral-border-weak)',
                        borderRadius: '8px',
                        background: 'var(--hbr-color-neutral-bg)',
                      }}
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        const atBottom =
                          el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
                        if (atBottom) setHasReachedBottom(true);
                      }}
                    >
                      <div className="hbr-css__layout-col-sm">
                        <div className="hbr-css__text-primary-sm-semibold">
                          {contentRecord?.title || contentRecord?.name || item?.name}
                        </div>
                        <div className="hbr-css__text-secondary-sm">
                          {hasReadableContentBody ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {contentBodyText}
                            </ReactMarkdown>
                          ) : (
                            'No content body available.'
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <StbButton
                      role="default"
                      disabled={!canMarkContentComplete}
                      onClick={handleMarkContentComplete}
                    >
                      {isCompleted ? 'Completed' : 'Mark Complete'}
                    </StbButton>
                  </div>
                </div>
              </HbrCard>
            )}
          </div>

          <div className="hbr-css__layout-row-sm hbr-css__layout-justify-between">
            <div>
              {previousItem ? (
                <StbButton role="default" onClick={async () => onPreviousItem(previousItem)}>
                  Previous Item
                </StbButton>
              ) : null}
            </div>

            <div className="hbr-css__layout-row-sm">
              {nextItem ? (
                <StbButton
                  role="default"
                  disabled={!canGoNext}
                  onClick={async () => onNextItem(nextItem)}
                >
                  Next Item
                </StbButton>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </StbModal>
  );
};

export default JourneyMapItemModal;